import React, { useState } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogActions,
  TextField,
  Typography,
  Stack,
} from "@mui/material";
import { motion } from "framer-motion";
import mainImage from "../../static/media/Booking/main.webp";

const PRIMARY = "#06BBC7";
const YELLOW = "#F7C948";
const YELLOW_HOVER = "#efbd35";
const BLACK = "#111827";

const MotionBox = motion(Box);
const MotionButton = motion(Button);

const AppointmentForm = () => {
  const [openModal, setOpenModal] = useState(false);

  const [formData, setFormData] = useState({
    fullName: "",
    phone: "",
    email: "",
    treatmentType: "",
    preferredDate: "",
    preferredTime: "",
    message: "",
  });

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (event) => {
    event.preventDefault();

    setOpenModal(true);

    setFormData({
      fullName: "",
      phone: "",
      email: "",
      treatmentType: "",
      preferredDate: "",
      preferredTime: "",
      message: "",
    });
  };

  const handleCloseModal = () => {
    setOpenModal(false);
  };

  return (
    <Box
      component="section"
      sx={{
        width: "100%",
        maxWidth: "100vw",
        position: "relative",
        bgcolor: PRIMARY,
        overflow: "hidden",
        boxSizing: "border-box",
        color: "#ffffff",
      }}
    >
      <Box
        component="svg"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        sx={{
          position: "absolute",
          top: -1,
          left: 0,
          width: "100%",
          height: { xs: 54, md: 86 },
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        <path
          d="M0,64L60,69.3C120,75,240,85,360,80C480,75,600,53,720,42.7C840,32,960,32,1080,42.7C1200,53,1320,75,1380,85.3L1440,96L1440,0L0,0Z"
          fill="#ffffff"
        />
      </Box>

      <Box
        component="svg"
        viewBox="0 0 1440 120"
        preserveAspectRatio="none"
        sx={{
          position: "absolute",
          bottom: -1,
          left: 0,
          width: "100%",
          height: { xs: 54, md: 86 },
          zIndex: 1,
          pointerEvents: "none",
        }}
      >
        <path
          d="M0,48L80,58.7C160,69,320,91,480,90.7C640,91,800,69,960,64C1120,59,1280,69,1360,74.7L1440,80L1440,120L0,120Z"
          fill="#ffffff"
        />
      </Box>

      <Box
        sx={{
          position: "absolute",
          width: { xs: 180, md: 360 },
          height: { xs: 180, md: 360 },
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.12)",
          top: { xs: 70, md: 120 },
          left: { xs: -90, md: -150 },
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          position: "absolute",
          width: { xs: 220, md: 420 },
          height: { xs: 220, md: 420 },
          borderRadius: "50%",
          bgcolor: "rgba(255,255,255,0.1)",
          right: { xs: -130, md: -180 },
          bottom: { xs: 90, md: 120 },
          pointerEvents: "none",
        }}
      />

      <Box
        sx={{
          width: "100%",
          maxWidth: 1200,
          mx: "auto",
          position: "relative",
          zIndex: 2,
          px: { xs: 2, sm: 3, md: 5 },
          pt: { xs: 11, sm: 12, md: 15 },
          pb: { xs: 11, sm: 12, md: 15 },
          boxSizing: "border-box",
        }}
      >
        <Box
          sx={{
            display: "grid",
            gridTemplateColumns: { xs: "1fr", md: "1.02fr 0.98fr" },
            gap: { xs: 4, md: 5 },
            alignItems: "center",
          }}
        >
          <MotionBox
            initial={{ opacity: 0, x: -34 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.48, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.25 }}
            sx={{
              width: "100%",
              minWidth: 0,
              order: { xs: 2, md: 1 },
            }}
          >
            <Typography
              component="p"
              sx={{
                m: 0,
                mb: 1.2,
                color: "rgba(255,255,255,0.86)",
                fontSize: { xs: "0.86rem", sm: "0.95rem" },
                fontWeight: 700,
                letterSpacing: "0.08em",
                textTransform: "uppercase",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Reserva tu espacio
            </Typography>

            <Typography
              component="h1"
              sx={{
                m: 0,
                mb: 1.7,
                color: "#ffffff",
                fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
                lineHeight: 1.05,
                fontWeight: 800,
                letterSpacing: "-0.04em",
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Agenda una cita para cuidar de ti
            </Typography>

            <Typography
              sx={{
                m: 0,
                mb: { xs: 3, md: 4 },
                color: "rgba(255,255,255,0.82)",
                fontSize: { xs: "0.96rem", sm: "1.04rem" },
                lineHeight: 1.75,
                fontWeight: 400,
                maxWidth: 560,
                mx: { xs: "auto", md: 0 },
                textAlign: { xs: "center", md: "left" },
              }}
            >
              Déjanos tus datos y revisaremos la disponibilidad para confirmar
              contigo el día y la hora de tu atención.
            </Typography>

            <Box
              component="form"
              onSubmit={handleSubmit}
              sx={{
                width: "100%",
                bgcolor: "#ffffff",
                borderRadius: { xs: "26px", md: "32px" },
                p: { xs: 2, sm: 2.6, md: 3 },
                display: "grid",
                gridTemplateColumns: { xs: "1fr", sm: "1fr 1fr" },
                gap: { xs: 2, sm: 2.2 },
                boxSizing: "border-box",
              }}
            >
              <TextField
                name="fullName"
                label="Nombre completo"
                value={formData.fullName}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                name="phone"
                label="Teléfono / WhatsApp"
                value={formData.phone}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                name="email"
                label="Correo electrónico"
                type="email"
                value={formData.email}
                onChange={handleChange}
                fullWidth
              />

              <TextField
                name="treatmentType"
                label="Tipo de tratamiento"
                value={formData.treatmentType}
                onChange={handleChange}
                required
                fullWidth
              />

              <TextField
                name="preferredDate"
                label="Fecha preferida"
                type="date"
                value={formData.preferredDate}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                name="preferredTime"
                label="Hora preferida"
                type="time"
                value={formData.preferredTime}
                onChange={handleChange}
                required
                fullWidth
                InputLabelProps={{
                  shrink: true,
                }}
              />

              <TextField
                name="message"
                label="Mensaje adicional"
                value={formData.message}
                onChange={handleChange}
                multiline
                minRows={4}
                fullWidth
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                }}
              />

              <Stack
                sx={{
                  gridColumn: { xs: "auto", sm: "1 / -1" },
                  alignItems: { xs: "stretch", sm: "flex-start" },
                  mt: 0.5,
                }}
              >
                <MotionButton
                  type="submit"
                  variant="contained"
                  whileHover={{ y: -4, scale: 1.025 }}
                  whileTap={{ scale: 0.96 }}
                  transition={{ duration: 0.22, ease: "easeOut" }}
                  sx={{
                    bgcolor: YELLOW,
                    color: BLACK,
                    px: 4,
                    py: 1.18,
                    width: { xs: "100%", sm: "auto" },
                    borderRadius: "999px",
                    textTransform: "none",
                    fontWeight: 800,
                    fontSize: "0.95rem",
                    boxShadow: "none",
                    "&:hover": {
                      bgcolor: YELLOW_HOVER,
                      boxShadow: "0 14px 28px rgba(247, 201, 72, 0.28)",
                    },
                  }}
                >
                  Enviar solicitud de cita
                </MotionButton>
              </Stack>
            </Box>
          </MotionBox>

          <MotionBox
            initial={{ opacity: 0, x: 34, scale: 0.96 }}
            whileInView={{ opacity: 1, x: 0, scale: 1 }}
            whileHover={{ y: -8, scale: 1.015 }}
            transition={{ duration: 0.5, ease: "easeOut" }}
            viewport={{ once: true, amount: 0.25 }}
            sx={{
              width: "100%",
              maxWidth: { xs: 430, sm: 520, md: 500 },
              mx: { xs: "auto", md: 0 },
              justifySelf: { xs: "center", md: "end" },
              order: { xs: 1, md: 2 },
              position: "relative",
              boxSizing: "border-box",
            }}
          >
            <Box
              sx={{
                position: "absolute",
                inset: { xs: "-12px 18px 18px -12px", md: "-18px 28px 28px -18px" },
                borderRadius: { xs: "32px", md: "42px" },
                bgcolor: "rgba(255,255,255,0.22)",
                transform: "rotate(-4deg)",
              }}
            />

            <Box
              sx={{
                position: "relative",
                width: "100%",
                borderRadius: { xs: "30px", md: "40px" },
                overflow: "hidden",
                bgcolor: "#ffffff",
                p: { xs: 1, sm: 1.2 },
                boxSizing: "border-box",
              }}
            >
              <Box
                component="img"
                src={mainImage}
                alt="Tratamiento corporal en cabina estética"
                sx={{
                  width: "100%",
                  height: { xs: 390, sm: 500, md: 660 },
                  objectFit: "cover",
                  objectPosition: "center",
                  display: "block",
                  borderRadius: { xs: "24px", md: "33px" },
                }}
              />
            </Box>

            <MotionBox
              initial={{ opacity: 0, y: 18 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.42, ease: "easeOut", delay: 0.2 }}
              viewport={{ once: true, amount: 0.35 }}
              sx={{
                position: { xs: "relative", sm: "absolute" },
                left: { xs: "auto", sm: -20 },
                bottom: { xs: "auto", sm: 32 },
                mt: { xs: 2, sm: 0 },
                bgcolor: "#ffffff",
                color: BLACK,
                borderRadius: "22px",
                px: { xs: 2, sm: 2.3 },
                py: { xs: 1.6, sm: 1.8 },
                width: { xs: "100%", sm: 245 },
                boxSizing: "border-box",
              }}
            >
              <Typography
                sx={{
                  color: PRIMARY,
                  fontSize: "0.82rem",
                  fontWeight: 800,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  mb: 0.5,
                }}
              >
                Atención personalizada
              </Typography>

              <Typography
                sx={{
                  color: "#4b5563",
                  fontSize: "0.92rem",
                  lineHeight: 1.55,
                  fontWeight: 500,
                }}
              >
                Tu cita queda pendiente hasta confirmar disponibilidad contigo.
              </Typography>
            </MotionBox>
          </MotionBox>
        </Box>
      </Box>

      <Dialog
        open={openModal}
        onClose={handleCloseModal}
        disableScrollLock
        PaperProps={{
          sx: {
            width: "100%",
            maxWidth: 440,
            mx: 2,
            borderRadius: "24px",
            p: { xs: 1, sm: 1.5 },
            boxShadow: "0 24px 60px rgba(0,0,0,0.2)",
          },
        }}
      >
        <DialogContent
          sx={{
            textAlign: "center",
            px: { xs: 2.2, sm: 3 },
            pt: { xs: 3, sm: 3.5 },
            pb: 1.5,
          }}
        >
          <Box
            sx={{
              width: 64,
              height: 64,
              borderRadius: "50%",
              bgcolor: "rgba(6,187,199,0.12)",
              color: PRIMARY,
              display: "grid",
              placeItems: "center",
              mx: "auto",
              mb: 2,
              fontSize: "2rem",
              fontWeight: 800,
            }}
          >
            ✓
          </Box>

          <Typography
            component="h2"
            sx={{
              m: 0,
              mb: 1.2,
              color: BLACK,
              fontSize: { xs: "1.45rem", sm: "1.65rem" },
              lineHeight: 1.2,
              fontWeight: 800,
            }}
          >
            Cita puesta en espera
          </Typography>

          <Typography
            sx={{
              m: 0,
              color: "#4b5563",
              fontSize: "0.98rem",
              lineHeight: 1.7,
              fontWeight: 400,
            }}
          >
            Tu solicitud ha sido recibida. Pronto nos pondremos en contacto
            contigo para certificar el día y la hora según disponibilidad.
          </Typography>
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "center",
            px: { xs: 2.2, sm: 3 },
            pb: { xs: 2.5, sm: 3 },
          }}
        >
          <Button
            onClick={handleCloseModal}
            variant="contained"
            sx={{
              bgcolor: PRIMARY,
              color: "#ffffff",
              px: 3.5,
              py: 1,
              borderRadius: "999px",
              textTransform: "none",
              fontWeight: 800,
              boxShadow: "none",
              "&:hover": {
                bgcolor: "#05a9b4",
                boxShadow: "none",
              },
            }}
          >
            Entendido
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AppointmentForm;